import { Stack, Typography, styled, useTheme } from "@mui/material";
import { addMonths, endOfMonth, format, subMonths } from "date-fns";
import { Fragment } from "react";
import type { DefaultLegendContentProps, TooltipContentProps } from "recharts";
import { humanizeNumber } from "../../lib/Util";
import { TEXTURES } from "../../styles/sbDesignSystem/Textures";

const LoadingContent = styled(Stack)(({ theme }) => ({
  height: "200px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  backgroundColor: theme.palette.surfaceLight10.main,
  border: `1px solid ${theme.palette.surfaceLight30.main}`,
  gap: "10px",
})) as typeof Stack;

const TooltipContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.surfaceLight30.main,
  color: theme.palette.text50.main,
  padding: "8px 16px 8px 16px",
  gap: "4px",
  borderRadius: "4px",
}));

type DatasetData = {
  month?: string;
  year?: number;
  production?: number;
  backfill?: number;
  proof?: number;
  contracted?: number;
  forecasted?: number;
  label?: string;
  [series: string]: string | number | undefined | null;
};
const ChartPatterns = (
  <defs>
    {Object.values(TEXTURES).map((texture) => (
      <Fragment key={texture.key}>{texture}</Fragment>
    ))}
  </defs>
);
const buildDataset = (contractedRowCount?: number | null) => {
  const dataset: Array<DatasetData> = [];

  const thirteenMonthsAgo = subMonths(new Date(), 13);
  let iter = thirteenMonthsAgo;
  let acc = 0;
  while (acc < 13) {
    dataset.push({
      month: format(iter, "MMM"),
      year: iter.getFullYear(),
      production: 0,
      backfill: 0,
      proof: 0,
      contracted: contractedRowCount || 0,
      label: format(iter, "MMM\u2018yy"),
    });
    iter = addMonths(iter, 1);
    acc = acc + 1;
  }
  dataset.push({
    month: format(iter, "MMM"),
    year: iter.getFullYear(),
    production: 0,
    backfill: 0,
    proof: 0,
    contracted: contractedRowCount || 0,
    label: format(iter, "MMM\u2018yy"),
  });

  return dataset;
};

// Passed to recharts as `content={<CustomLegend />}`, so recharts injects the props.
const CustomLegend = (props: Partial<DefaultLegendContentProps>) => {
  const { payload } = props;
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      sx={{
        gap: "32px",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        position: "relative",
        top: "40px",
        ml: "60px",
      }}
    >
      {payload?.map((entry) => (
        <Stack key={String(entry.dataKey)} direction="row" sx={{ gap: "12px", alignItems: "center" }}>
          <svg width="24" height="24">
            <rect fill={theme.palette.surfaceLight10.main} width="24" height="24" rx="2px" />
            <rect fill={entry.color} width="24" height="24" rx="2px" />
          </svg>
          <Typography variant="body.small.regular" sx={{ color: "text50.main" }}>
            {entry.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const CustomTooltip = (props: Partial<TooltipContentProps>) => {
  const { active, payload } = props;
  const theme = useTheme();
  if (!active || !payload || !payload.length) return null;
  return (
    <TooltipContainer>
      {payload.map((p) => (
        <Fragment key={String(p.dataKey ?? p.name)}>
          <Stack direction="row" sx={{ gap: "10px", alignItems: "center" }}>
            <svg width="17" height="17">
              <circle fill={theme.palette.text50.main} cx="8.5" cy="8.5" r="8.5" />
              <circle fill={p.name === "Contracted" ? p.stroke : p.fill} cx="8.5" cy="8.5" r="8.5" />
            </svg>
            <Typography variant="body.small.regular">{p.name}</Typography>
          </Stack>
          <Typography variant="body.xSmall.regular" sx={{ color: "text40.main" }}>
            {humanizeNumber(p.value as number)}
          </Typography>
        </Fragment>
      ))}
    </TooltipContainer>
  );
};

const getRangeDate = () => {
  const today = new Date();
  const startingMonth = subMonths(today, 13);
  const startingDay = format(startingMonth, "MM/01/yyyy");
  const endingDay = format(today, `MM/${format(endOfMonth(today), "dd")}/yyyy`);
  return { startingDay, endingDay };
};

export { ChartPatterns, CustomLegend, CustomTooltip, DatasetData, LoadingContent, buildDataset, getRangeDate };
